import { useMemo, useState, useEffect } from 'react';
// import { useViewport } from '@/hooks';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  MRT_GlobalFilterTextField,
  MRT_ToggleFiltersButton,
} from 'material-react-table';
import {
  Box,
  Button,
  Typography,
  useTheme,
  // LinearProgress,
  // Tooltip,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid';

// Helper function to convert GridColDef to MRT_ColumnDef
const convertGridColDefToMRT = <T extends Record<string, any>>(gridCol: GridColDef): MRT_ColumnDef<T> => {
  const mrtCol: any = {
    header: gridCol.headerName || gridCol.field || '',
    size: gridCol.width,
    enableSorting: true,// gridCol.sortable !== false,
    enableColumnFilter: gridCol.filterable !== false,
  };

  // Set accessorKey or accessorFn based on field availability
  if (gridCol.field) {
    mrtCol.accessorKey = gridCol.field;
  } else {
    mrtCol.id = `column_${Math.random().toString(36).substring(2, 11)}`;
  }

  // Handle custom render cell
  if (gridCol.renderCell) {
    mrtCol.Cell = ({ cell, row }: any) => {
      return gridCol.renderCell!({
        value: cell.getValue(),
        row: row.original,
        api: {} as any,
        field: gridCol.field || '',
        id: row.id,
        getValue: (field: string) => (row.original as any)[field],
      } as any);
    };
  }

  // Handle value getter - if present, use accessorFn instead of accessorKey
  if (gridCol.valueGetter) {
    delete mrtCol.accessorKey; // Remove accessorKey when using accessorFn
    mrtCol.id = gridCol.field || `column_${Math.random().toString(36).substring(2, 11)}`;
    mrtCol.accessorFn = (row: T) => {
      try {
        const mockGridRowParams = {
          row: row,
          value: (row as any)[gridCol.field || ''],
          getValue: (field: string) => (row as any)[field],
          api: {} as any,
        };
        // Use type assertion to bypass strict typing
        const valueGetter = gridCol.valueGetter as any;
        return valueGetter(
          (row as any)[gridCol.field || ''], // value
          mockGridRowParams, // row params
          gridCol, // column
          {} as any // api
        );
      } catch (error) {
        console.warn('Error in valueGetter conversion:', error);
        return (row as any)[gridCol.field || ''];
      }
    };
  }

  return mrtCol as MRT_ColumnDef<T>;
};

export interface ReusableDataGridProps<T extends Record<string, any> = any> {
  columns: MRT_ColumnDef<T>[] | GridColDef[];
  rows: T[];
  loading?: boolean;
  paginationModel?: {
    page: number;
    pageSize: number;
  };
  onPaginationModelChange?: (model: { page: number; pageSize: number }) => void;
  onSortingChange?: (sorting: { id: string; desc: boolean }[]) => void;
  paginationData?: {
    totalRecords: number;
  };
  noRowsMessage?: string;
  noRowsActionLabel?: string;
  onNoRowsAction?: () => void;
  showSerialNumber?: boolean;
  hideFooter?: boolean;
  enableServerSidePagination?: boolean;
  enableRowSelection?: boolean;
  enableColumnFilters?: boolean;
  enableGlobalFilter?: boolean;
  enableSorting?: boolean;
  onRowSelectionChange?: (selectedRows: T[]) => void;
  getRowId?: (row: T) => string | number;
  onRowClick?: (params: { row: T }) => void;
  tableMaxHeight?: number | string;
  enableRowVirtualization?: boolean;
  enableStickyHeader?: boolean;
  rowClickTooltip?: string;
  getRowStyles?: (row: T) => React.CSSProperties;
  getRowTooltip?: (row: T) => string | null;
}

export const ReusableDataGrid = <T extends Record<string, any> = any>({
  columns,
  rows,
  loading = false,
  paginationModel = { page: 0, pageSize: 10 },
  onPaginationModelChange,
  // onSortingChange,
  paginationData,
  noRowsMessage = 'No data found',
  noRowsActionLabel,
  onNoRowsAction,
  showSerialNumber = true,
  hideFooter = false,
  enableServerSidePagination = true,
  enableRowSelection = false,
  enableColumnFilters = false,
  enableGlobalFilter = false,
  enableSorting = true,
  onRowSelectionChange,
  getRowId,
  onRowClick,
  // tableMaxHeight,
  enableRowVirtualization = false,
  enableStickyHeader = true,
  getRowStyles,
  getRowTooltip,
  // rowClickTooltip,
}: ReusableDataGridProps<T>) => {
  const theme = useTheme();
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  // Clear selection when data changes
  useEffect(() => {
    setRowSelection({});
  }, [rows]);
  // const { height, isMobile } = useViewport();
  
  // Calculate dynamic table height based on DashboardLayout structure
  // const _dynamicTableHeight = useMemo(() => {
  //   if (tableMaxHeight) return tableMaxHeight;
    
  //   // DashboardLayout breakdown (responsive):
  //   // - Toolbar: ~64px
  //   // - Container padding (pt: 2): ~16px  
  //   // - Paper padding (py: 3): ~48px (24px top + 24px bottom)
  //   // - Page header/title area: mobile: ~60px, desktop: ~80px
  //   // - Tabs area: mobile: ~48px, desktop: ~60px
  //   // - Dividers and margins: mobile: ~24px, desktop: ~32px
  //   // - Footer pagination (if shown): ~60px
  //   // - Bottom margin/padding: ~24px
    
  //   const toolbarHeight = 64;
  //   const containerPadding = 16;
  //   const paperPadding = 48;
  //   const pageHeaderHeight = isMobile ? 60 : 80;
  //   const tabsHeight = isMobile ? 48 : 60;
  //   const marginsAndDividers = isMobile ? 24 : 32;
  //   const paginationFooter = hideFooter ? 0 : 60;
  //   const bottomSpacing = 24;
    
  //   const totalReservedSpace = toolbarHeight + containerPadding + paperPadding + 
  //                              pageHeaderHeight + tabsHeight + marginsAndDividers + 
  //                              paginationFooter + bottomSpacing;
    
  //   const calculatedHeight = height - totalReservedSpace;
    
  //   // Responsive min/max heights
  //   const minHeight = isMobile ? 250 : 300;
  //   const maxHeightRatio = isMobile ? 0.75 : 0.8;
  //   const maxHeight = Math.floor(height * maxHeightRatio);
    
  //   return Math.max(minHeight, Math.min(calculatedHeight, maxHeight));
  // }, [tableMaxHeight, height, hideFooter, isMobile]);
  // Create serial number column
  const serialNumberColumn: MRT_ColumnDef<T> = useMemo(() => ({
    accessorFn: (originalRow: T) => originalRow,
    id: 'serialNumber',
    header: 'S.NO',
    size: 70,
    enableSorting: false,
    enableColumnFilter: false,
    enableGlobalFilter: false,
    Cell: ({ row }) => {
      const page = paginationModel?.page || 0;
      const pageSize = paginationModel?.pageSize || 10;
      const serialNumber = enableServerSidePagination 
        ? page * pageSize + row.index + 1 
        : row.index + 1;
      
      return (
        <Typography variant="body2" fontWeight="medium" pl={1}>
          {serialNumber}
        </Typography>
      );
    },
  }), [paginationModel, enableServerSidePagination]);

  // Convert GridColDef to MRT_ColumnDef if needed
  const convertedColumns = useMemo(() => {
    return columns.map(col => {
      // Check if it's a GridColDef (has field property)
      if ('field' in col) {
        return convertGridColDefToMRT<T>(col as GridColDef);
      }
      return col as MRT_ColumnDef<T>;
    });
  }, [columns]);

  // Combine columns with serial number if enabled
  const finalColumns = useMemo(() => 
    showSerialNumber ? [serialNumberColumn, ...convertedColumns] : convertedColumns,
    [showSerialNumber, serialNumberColumn, convertedColumns]
  );

  const table = useMaterialReactTable({
    columns: finalColumns,
    data: rows,
    enableColumnFilterModes: enableColumnFilters,
    enableColumnOrdering: false,
    enableColumnActions: false,
    enableGrouping: false,
    enableColumnPinning: true,
    enableFacetedValues: false,
    enableRowSelection,
    enableSorting: enableSorting,
    enableGlobalFilter: enableGlobalFilter,
    enableTopToolbar: enableGlobalFilter || enableColumnFilters,
    enableColumnFilters: enableColumnFilters,
    enableBottomToolbar: !hideFooter,
    enablePagination: true,
    enableRowVirtualization: enableRowVirtualization,
    enableStickyHeader: enableStickyHeader,
    // enableBottomToolbar: !hideFooter,
    getRowId: getRowId ? (row: T) => String(getRowId(row)) : undefined,
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: enableGlobalFilter,
      // columnPinning: {
      //   left: enableRowSelection ? ['mrt-row-select'] : [],
      //   right: rowActions.length > 0 ? ['mrt-row-actions'] : [],
      // },
      pagination: {
        pageIndex: paginationModel.page,
        pageSize: paginationModel.pageSize,
      },
    },
    manualPagination: enableServerSidePagination,
    manualSorting: false,
    manualFiltering: enableServerSidePagination,
    rowCount: enableServerSidePagination ? (paginationData?.totalRecords || 0) : rows.length,
    onPaginationChange: (updater) => {
      if (onPaginationModelChange && typeof updater === 'function') {
        const newPagination = updater({ pageIndex: paginationModel.page, pageSize: paginationModel.pageSize });
        onPaginationModelChange({ page: newPagination.pageIndex, pageSize: newPagination.pageSize });
      }
    },
    // onSortingChange: (updater) => {
    //   console.log(updater)
    //   // if (onSortingChange && typeof updater === 'function') {
    //   //   const currentSorting = table.getState().sorting;
    //   //   const newSorting = updater(currentSorting);
    //   //   console.log(table.getState().sorting)
    //   //   onSortingChange(newSorting);
    //   // }
    // },
    onRowSelectionChange: (updater) => {
      const newSelection = typeof updater === 'function' ? updater(rowSelection) : updater;
      setRowSelection(newSelection);
      
      if (onRowSelectionChange) {
        // Get selected rows based on the new selection state
        const selectedRows = Object.keys(newSelection)
          .filter(key => newSelection[key])
          .map(key => {
            const row = table.getRowModel().rowsById[key];
            return row?.original;
          })
          .filter(Boolean);
        
        onRowSelectionChange(selectedRows);
      }
    },
    state: {
      isLoading: loading,
      pagination: {
        pageIndex: paginationModel.page,
        pageSize: paginationModel.pageSize,
      },
      rowSelection,
    },
    paginationDisplayMode: hideFooter ? 'default' : 'pages',
    muiTableProps: {
      sx: {
        backgroundColor: theme.palette.background.paper,
        boxShadow:'none !important'
      },
    },
    muiTableContainerProps: {
      sx: {
        // maxHeight: dynamicTableHeight,
        // minHeight:dynamicTableHeight-40,
    
        overflow: 'auto',
      },
    },
    muiCircularProgressProps:{
      sx: {

display:'none'
      }
    },
   
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
        // borderBottom: '1px solid #e0e0e0',
        fontWeight: 600,
        color: theme.palette.text.primary,
        textAlign: 'left',
        alignContent:'center',
        height:'60px',
        ...(enableStickyHeader && {
          position: 'sticky',
          top: 0,
          zIndex: 10,
          boxShadow:'none !important'

          // boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }),
      },
    },
    muiTableBodyCellProps: ({ row }) => {
      const customStyles = getRowStyles ? getRowStyles(row.original) : {};
      return {
        sx: {
          backgroundColor: customStyles.backgroundColor || theme.palette.background.paper,
          // borderBottom: '1px solid #e0e0e0',
          padding: 1.75,
          textAlign: 'left',
          color: theme.palette.text.primary,
          '&:hover': {
            // backgroundColor: customStyles.backgroundColor ? '#ffcdd2' : '#f9f9f9',
          },
        },
      };
    },
    muiTableBodyRowProps: ({ row }) => {
      const customStyles = getRowStyles ? getRowStyles(row.original) : {};
      const baseBackgroundColor = customStyles.backgroundColor || 'white';
      const tooltipText = getRowTooltip ? getRowTooltip(row.original) : null;
      return {
        onClick: onRowClick ? () => onRowClick({ row: row.original }) : undefined,
        title: tooltipText || undefined,
        sx: {
          backgroundColor: baseBackgroundColor,
          boxShadow: 'none !important',
          '&:hover': {
            backgroundColor: customStyles.backgroundColor ? (theme.palette.mode === 'dark' ? theme.palette.error.dark : '#ffcdd2') : (theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[100]),
            transition: 'all 0.2s ease-in-out',
          },
          cursor: onRowClick ? 'pointer' : 'default',
          ...customStyles,
        },
      };
    },
    muiPaginationProps: {
      color: 'primary',
      rowsPerPageOptions: [5, 10,15, 20,25],
      shape: 'rounded',
      variant: 'outlined',
    },
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
      placeholder: 'Search...',
    },
    renderEmptyRowsFallback: () => (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="200px"
        gap={2}
      >
        <Typography variant="body2" color="text.secondary">
          {noRowsMessage}
        </Typography>
        {noRowsActionLabel && onNoRowsAction && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onNoRowsAction}
          >
            {noRowsActionLabel}
          </Button>
        )}
      </Box>
    ),
    renderTopToolbar: ({ table }) => (
      <Box
        sx={{
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
          display: 'flex',
          gap: '0.5rem',
          p: '8px',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {enableGlobalFilter && <MRT_GlobalFilterTextField table={table} />}
          {enableColumnFilters && <MRT_ToggleFiltersButton table={table} />}
        </Box>
        {enableRowSelection && (
          <Typography variant="body2" color="text.secondary">
            {table.getSelectedRowModel().flatRows.length} of {table.getFilteredRowModel().rows.length} row(s) selected
          </Typography>
        )}
      </Box>
    ),
    enableRowActions: false,
    renderBottomToolbar: hideFooter ? () => null : undefined,
  });

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        position: 'relative',
        minHeight: '400px',
        '& .MuiTableRow-root':{
          boxShadow:'none !important'
        },
        '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': {
          borderBottom: 'none'
        }
      }}>
        {/* {onRowClick ? (
          <Tooltip 
            title={rowClickTooltip} 
            placement="top-start"
            arrow
          >
            <div>
              <MaterialReactTable table={table} />
            </div>
          </Tooltip>
        ) : ( */}
          <MaterialReactTable table={table} />
        {/* )} */}
        {/* {loading && (
          <LinearProgress
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1,
            }}
          />
        )} */}
      </Box>
    </Box>
  );
};